import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Col, Grid, message, Modal, Row } from 'antd';
import { Formik, type FormikConfig } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type InsurerDetailsDataFragment } from '../../../api/fragments/InsurerDetailsData';
import {
    DeleteInsurerDocument,
    type DeleteInsurerMutation,
    type DeleteInsurerMutationVariables,
} from '../../../api/mutations/deleteInsurer';
import {
    UpdateEazyInsurerIntegrationSettingDocument,
    type UpdateEazyInsurerIntegrationSettingMutation,
    type UpdateEazyInsurerIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateEazyInsurerIntegrationSetting';
import {
    UpdateEmailInsurerIntegrationSettingDocument,
    type UpdateEmailInsurerIntegrationSettingMutation,
    type UpdateEmailInsurerIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateEmailInsurerIntegrationSetting';
import {
    UpdateInsurerDocument,
    type UpdateInsurerMutation,
    type UpdateInsurerMutationVariables,
} from '../../../api/mutations/updateInsurer';
import {
    type EazyInsurerIntegration,
    type EmailInsurerIntegration,
    type InsuranceProductType,
    InsurerIntegrationProvider,
    type InsurerSettings,
} from '../../../api/types';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import {
    EditInsurerForm,
    InsuranceProductTypeForm,
    InsurerIntegration,
    type InsurerIntegrationValues,
    useInsurerFormValidator,
} from './InsurerForm';
import useInsurerIntegrationFormValidation from './InsurerForm/useInsurerIntegrationFormValidation';

export type InsurerDetailsInnerPageProps = { insurer: InsurerDetailsDataFragment };

type InsurerFormValues = InsurerSettings & {
    integration: InsurerIntegrationValues;
    insuranceProductTypes: InsuranceProductType[];
};

const InsurerDetailsInnerPage = ({ insurer }: InsurerDetailsInnerPageProps) => {
    const { t } = useTranslation(['insurerDetails', 'financeProductDetails']);
    const insurerId = insurer.id;

    const apolloClient = useApolloClient();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: insurer.versioning,
        moduleId: insurer.moduleId,
    });
    const screens = Grid.useBreakpoint();

    const navigate = useNavigate();
    const onSubmitMainDetails = useHandleError<InsurerFormValues>(
        async values => {
            const { ...mainDetailsValues } = values;
            const { integration, ...settings } = mainDetailsValues;

            // submitting message
            message.loading({
                content: t('insurerDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateInsurerMutation, UpdateInsurerMutationVariables>({
                    mutation: UpdateInsurerDocument,
                    variables: { insurerId, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('insurerDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/insurers');
        },
        [apolloClient, insurerId, navigate, t]
    );

    const onSubmitIntegration = useHandleError<InsurerFormValues>(
        async values => {
            const { provider, ...settings } = values.integration;

            // submitting message
            message.loading({
                content: t('insurerDetails:messages.updateInsurerIntegrationsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            switch (provider) {
                case InsurerIntegrationProvider.Email: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateEmailInsurerIntegrationSettingMutation,
                            UpdateEmailInsurerIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateEmailInsurerIntegrationSettingDocument,
                            variables: { insurerId, settings: settings as EmailInsurerIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case InsurerIntegrationProvider.Eazy: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateEazyInsurerIntegrationSettingMutation,
                            UpdateEazyInsurerIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateEazyInsurerIntegrationSettingDocument,
                            variables: { insurerId, settings: settings as EazyInsurerIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                default:
                    break;
            }

            // inform about success
            message.success({
                content: t('insurerDetails:messages.updateInsurerIntegrationSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/insurers');
        },
        [apolloClient, insurerId, navigate, t]
    );

    const onSubmit: FormikConfig<InsurerFormValues>['onSubmit'] = useCallback(
        (values, helpers) => {
            onSubmitMainDetails(values, helpers);
            onSubmitIntegration(values, helpers);

            return null;
        },
        [onSubmitIntegration, onSubmitMainDetails]
    );

    const deleteInsurer = useCallback(() => {
        Modal.confirm({
            title: t('insurerDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('insurerDetails:deleteModal.content'),
            okText: t('insurerDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('insurerDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('insurerDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteInsurerMutation, DeleteInsurerMutationVariables>({
                            mutation: DeleteInsurerDocument,
                            variables: { insurerId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('insurerDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/insurers');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, insurerId, navigate, t]);

    const initialValues = useMemo(
        (): InsurerFormValues => ({
            ...pick(
                [
                    'displayName',
                    'legalName',
                    'moduleId',
                    'email',
                    'order',
                    'submissionApprovalModuleId',
                    'reSubmissionApprovalModuleId',
                    'insuranceDisclaimer',
                    'sendEmailToCustomer',
                    'sendPDFToCustomer',
                    'showCommentsField',
                    'isActive',
                    'integration',
                    'insuranceProductTypes',
                ],
                insurer
            ),
        }),
        [insurer]
    );

    const initialIntegrationValues = useMemo((): InsurerIntegrationValues => insurer.integration, [insurer]);

    const validateMainDetails = useInsurerFormValidator(true);
    const validateIntegration = useInsurerIntegrationFormValidation(initialIntegrationValues.provider);

    const validate = useCallback(
        (values: InsurerFormValues) => {
            const mainErrors = validateMainDetails(values);
            const integrationErrors = validateIntegration(values.integration);

            return {
                ...mainErrors,
                ...integrationErrors,
            };
        },
        [validateIntegration, validateMainDetails]
    );

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(insurer.permissions, [permissionKind.updateInsurer]),
            hasPermissions(insurer.permissions, [permissionKind.deleteInsurer]),
        ],
        [insurer.permissions]
    );

    return (
        <ConsolePageWithHeader
            extra={
                insurer &&
                hasDeletePermission && (
                    <Button onClick={deleteInsurer} danger>
                        {t('insurerDetails:actions.delete')}
                    </Button>
                )
            }
            footer={
                hasUpdatePermission && [
                    <Button key="submit" form="insurerForm" htmlType="submit" type="primary">
                        {t('insurerDetails:actions.update')}
                    </Button>,
                ]
            }
            onBack={() => navigate('/admin/insurers')}
            title={screens.md ? t('insurerDetails:title', { name: insurer.displayName }) : insurer.displayName}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="insurerForm" name="insurerForm" onSubmitCapture={handleSubmit}>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <EditInsurerForm
                                    disabled={!hasUpdatePermission}
                                    insurer={insurer}
                                    offset={offset}
                                    updated={updated}
                                />
                            </Col>
                            <Col span={24}>
                                <InsuranceProductTypeForm disabled={!hasUpdatePermission} insurer={insurer} isUpdate />
                            </Col>

                            <Col span={24}>
                                <InsurerIntegration
                                    disabled={!hasUpdatePermission}
                                    provider={initialIntegrationValues.provider}
                                    isUpdate
                                />
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default InsurerDetailsInnerPage;
